import { api } from './utils';

// IndexedDB database name and version
const DB_NAME = 'roch-offline-db';
const DB_VERSION = 1;

// Store names
const STORES = {
  REVENUES: 'revenues',
  EXPENSES: 'expenses',
  RECEIVABLES: 'receivables',
  INGREDIENTS: 'ingredients',
  SYNC_QUEUE: 'sync_queue',
  META: 'meta'
};

// Open IndexedDB
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create stores
      Object.values(STORES).forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
        }
      });
    };
  });
};

// Save data to IndexedDB
export const saveOffline = async (storeName, data) => {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  
  const item = {
    ...data,
    id: data.id || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    offline: true,
    created_at: data.created_at || new Date().toISOString()
  };
  
  store.put(item);
  
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(item);
    tx.onerror = () => reject(tx.error);
  });
};

// Get all items from a store
export const getOfflineData = async (storeName) => {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Clear a store
export const clearOfflineStore = async (storeName) => {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  store.clear();
  
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Check if online
export const isOnline = () => navigator.onLine;

// Sync offline data to server
export const syncToServer = async () => {
  if (!isOnline()) {
    return { success: false, message: 'No internet connection' };
  }
  
  try {
    // Get all offline data
    const revenues = await getOfflineData(STORES.REVENUES);
    const expenses = await getOfflineData(STORES.EXPENSES);
    const receivables = await getOfflineData(STORES.RECEIVABLES);
    const ingredients = await getOfflineData(STORES.INGREDIENTS);
    
    // Filter only offline items
    const offlineRevenues = revenues.filter(r => r.offline);
    const offlineExpenses = expenses.filter(e => e.offline);
    const offlineReceivables = receivables.filter(r => r.offline);
    const offlineIngredients = ingredients.filter(i => i.offline);
    
    if (offlineRevenues.length === 0 && offlineExpenses.length === 0 && 
        offlineReceivables.length === 0 && offlineIngredients.length === 0) {
      return { success: true, message: 'Nothing to sync', synced: 0 };
    }
    
    // Upload to server
    const response = await api.post('/sync/upload', {
      revenues: offlineRevenues.map(r => ({ ...r, offline: undefined })),
      expenses: offlineExpenses.map(e => ({ ...e, offline: undefined })),
      receivables: offlineReceivables.map(r => ({ ...r, offline: undefined })),
      ingredients: offlineIngredients.map(i => ({ ...i, offline: undefined }))
    });
    
    // Clear synced data
    if (response.data.total > 0) {
      await clearOfflineStore(STORES.REVENUES);
      await clearOfflineStore(STORES.EXPENSES);
      await clearOfflineStore(STORES.RECEIVABLES);
      await clearOfflineStore(STORES.INGREDIENTS);
    }
    
    // Save last sync time
    const db = await openDB();
    const tx = db.transaction(STORES.META, 'readwrite');
    const store = tx.objectStore(STORES.META);
    store.put({ id: 'lastSync', time: new Date().toISOString() });
    
    return {
      success: true,
      message: `Synced ${response.data.total} items`,
      synced: response.data.total,
      details: response.data.synced
    };
  } catch (error) {
    console.error('Sync error:', error);
    return { success: false, message: error.message || 'Sync failed' };
  }
};

// Download data from server for offline use
export const downloadFromServer = async () => {
  if (!isOnline()) {
    return { success: false, message: 'No internet connection' };
  }
  
  try {
    // Get last sync time
    const db = await openDB();
    const tx = db.transaction(STORES.META, 'readonly');
    const store = tx.objectStore(STORES.META);
    const lastSyncRequest = store.get('lastSync');
    
    const lastSync = await new Promise((resolve) => {
      lastSyncRequest.onsuccess = () => resolve(lastSyncRequest.result?.time);
      lastSyncRequest.onerror = () => resolve(null);
    });
    
    // Download from server
    const response = await api.get('/sync/download', {
      params: { since: lastSync }
    });
    
    const data = response.data;
    
    // Store ventures and locations in localStorage for quick access
    localStorage.setItem('roch_ventures', JSON.stringify(data.ventures));
    localStorage.setItem('roch_locations', JSON.stringify(data.locations));
    
    return {
      success: true,
      message: 'Data downloaded',
      data: {
        ventures: data.ventures.length,
        locations: data.locations.length,
        revenues: data.revenues.length,
        expenses: data.expenses.length
      }
    };
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, message: error.message || 'Download failed' };
  }
};

// Get offline ventures (from localStorage)
export const getOfflineVentures = () => {
  try {
    return JSON.parse(localStorage.getItem('roch_ventures') || '[]');
  } catch {
    return [];
  }
};

// Get offline locations (from localStorage)
export const getOfflineLocations = () => {
  try {
    return JSON.parse(localStorage.getItem('roch_locations') || '[]');
  } catch {
    return [];
  }
};

// Add listener for online/offline events
export const initOfflineSync = (onStatusChange) => {
  const handleOnline = async () => {
    onStatusChange?.({ online: true });
    // Auto-sync when coming back online
    const result = await syncToServer();
    if (result.synced > 0) {
      onStatusChange?.({ online: true, synced: result.synced });
    }
  };
  
  const handleOffline = () => {
    onStatusChange?.({ online: false });
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// Save revenue offline
export const saveRevenueOffline = (revenue) => saveOffline(STORES.REVENUES, revenue);

// Save expense offline
export const saveExpenseOffline = (expense) => saveOffline(STORES.EXPENSES, expense);

// Save receivable offline
export const saveReceivableOffline = (receivable) => saveOffline(STORES.RECEIVABLES, receivable);

// Save ingredient offline
export const saveIngredientOffline = (ingredient) => saveOffline(STORES.INGREDIENTS, ingredient);

// Get pending offline count
export const getPendingOfflineCount = async () => {
  const revenues = await getOfflineData(STORES.REVENUES);
  const expenses = await getOfflineData(STORES.EXPENSES);
  const receivables = await getOfflineData(STORES.RECEIVABLES);
  const ingredients = await getOfflineData(STORES.INGREDIENTS);
  
  return {
    revenues: revenues.filter(r => r.offline).length,
    expenses: expenses.filter(e => e.offline).length,
    receivables: receivables.filter(r => r.offline).length,
    ingredients: ingredients.filter(i => i.offline).length,
    total: revenues.filter(r => r.offline).length + 
           expenses.filter(e => e.offline).length + 
           receivables.filter(r => r.offline).length + 
           ingredients.filter(i => i.offline).length
  };
};
